/**
 * Header Mega Menu — Interaction Engine
 *
 * Uses GSAP for all panel/image/list animations.
 * Centering: panel `left: 50%` is resolved against the full-width
 * `.section-header` (position: sticky) rather than the narrow nav.
 * GSAP sets `x: '-50%'` (= translateX(-50%)) on every open to
 * centre the panel without conflicting with Y-axis slide animation.
 *
 * Data attributes needed in header-v2.liquid:
 *   [data-mmenu-scope]   on <nav.header__inline-menu>
 *   [data-mmenu-trigger] on each .submenu_wrapper div
 *   [data-mmenu-panel]   on each .mega-menu__container div
 */

(function initHeaderMegaMenu() {
  'use strict';

  const BREAKPOINT_DESKTOP = 990;
  const HOVER_OPEN_DELAY   = 80;  // ms intent delay before opening
  const HOVER_CLOSE_DELAY  = 250; // ms grace period before closing — wide enough to
                                  // cover the gap between nav bar and panel top edge

  /* ──────────────────────────────────────────────────
     GSAP GUARD
     Script is `defer`ed so GSAP may load after this file.
     Poll briefly and bail after ~2 s.
  ────────────────────────────────────────────────── */

  function waitForGsap(attempts, callback) {
    if (typeof window.gsap !== 'undefined') { callback(window.gsap); return; }
    if (attempts <= 0) return;
    setTimeout(() => waitForGsap(attempts - 1, callback), 100);
  }

  /* ──────────────────────────────────────────────────
     MAIN INIT
  ────────────────────────────────────────────────── */

  function init(gsap) {
    const scope = document.querySelector('[data-mmenu-scope]');
    if (!scope) return;

    const triggers = Array.from(scope.querySelectorAll('[data-mmenu-trigger]'));
    if (!triggers.length) return;

    const state = {
      activeTrigger:  null,
      openTimer:      null,
      closeTimer:     null,
      tabSwitchTimer: null,
    };

    /* ────────────────────────────────────────────
       HELPERS
    ──────────────────────────────────────────── */

    function isDesktop() { return window.innerWidth >= BREAKPOINT_DESKTOP; }
    function getPanelOf(trigger) { return trigger.querySelector('[data-mmenu-panel]'); }
    function getTriggerLink(trigger) { return trigger.querySelector('.parent__header__menu-item'); }

    function clearTimers() {
      clearTimeout(state.openTimer);
      clearTimeout(state.closeTimer);
      clearTimeout(state.tabSwitchTimer);
      state.openTimer = state.closeTimer = state.tabSwitchTimer = null;
    }

    /* ────────────────────────────────────────────
       OPEN PANEL
    ──────────────────────────────────────────── */

    function openPanel(trigger) {
      if (!isDesktop()) return;

      const panel = getPanelOf(trigger);
      if (!panel) return;

      // ── Case 1: same panel is currently closing → reverse animation ──
      if (state.activeTrigger === trigger) {
        if (panel.dataset.mmenuClosing) {
          delete panel.dataset.mmenuClosing;
          gsap.killTweensOf(panel);
          gsap.to(panel, { opacity: 1, y: 0, duration: 0.18, ease: 'power2.out' });
          const link = getTriggerLink(trigger);
          if (link) link.setAttribute('aria-expanded', 'true');
        }
        return;
      }

      // ── Case 2: different panel open → close it immediately ──
      if (state.activeTrigger) closePanelImmediate(state.activeTrigger);

      state.activeTrigger = trigger;

      const link = getTriggerLink(trigger);
      if (link) link.setAttribute('aria-expanded', 'true');

      // Set initial state before display kicks in, then animate in
      gsap.killTweensOf(panel);
      gsap.set(panel, { x: '-50%', y: -14, opacity: 0 });
      panel.classList.add('is-open');

      gsap.to(panel, { y: 0, opacity: 1, duration: 0.28, ease: 'power3.out' });

      // Fade in the initially active image (producten layout)
      const activeImage = panel.querySelector('.mega-menu__image-wrapper.is-active');
      if (activeImage) {
        gsap.killTweensOf(activeImage);
        gsap.set(activeImage, { opacity: 0 });
        gsap.to(activeImage, { opacity: 1, duration: 0.32, delay: 0.06, ease: 'power2.out' });
      }
    }

    /* ────────────────────────────────────────────
       CLOSE PANEL
    ──────────────────────────────────────────── */

    function closePanel() {
      if (!state.activeTrigger) return;
      const trigger = state.activeTrigger;
      const panel   = getPanelOf(trigger);

      // NOTE: keep activeTrigger set during close animation so that re-hovering
      // the same trigger/panel can detect and reverse the close (prevents flicker).
      const link = getTriggerLink(trigger);
      if (link) link.setAttribute('aria-expanded', 'false');
      if (!panel) return;

      panel.dataset.mmenuClosing = 'true';

      gsap.killTweensOf(panel);
      gsap.to(panel, {
        opacity: 0,
        y: -10,
        duration: 0.20,
        ease: 'power2.in',
        onComplete() {
          if (panel.dataset.mmenuClosing === 'true') {
            delete panel.dataset.mmenuClosing;
            state.activeTrigger = null;
            panel.classList.remove('is-open');
            gsap.set(panel, { clearProps: 'all' });
          }
        },
      });
    }

    /* ────────────────────────────────────────────
       CLOSE PANEL IMMEDIATE (panel switch / resize)
    ──────────────────────────────────────────── */

    function closePanelImmediate(trigger) {
      const panel = getPanelOf(trigger);
      const link  = getTriggerLink(trigger);
      if (link) link.setAttribute('aria-expanded', 'false');
      if (!panel) return;

      gsap.killTweensOf(panel);
      panel.classList.remove('is-open');
      gsap.set(panel, { clearProps: 'all' });

      // Reset tab-switch state
      panel.querySelectorAll('.grandchildren-list').forEach((l) => {
        gsap.killTweensOf(l);
        l.classList.remove('is-active', 'is-leaving');
        gsap.set(l, { clearProps: 'opacity' });
      });
      panel.querySelectorAll('.mega-menu__image-wrapper').forEach((img) => {
        gsap.killTweensOf(img);
        img.classList.remove('is-active', 'is-leaving');
        gsap.set(img, { clearProps: 'opacity' });
      });
    }

    /* ────────────────────────────────────────────
       TAB SWITCHING — producten layout
       Hover on level-1 item → swap col-2 list + col-3 image
    ──────────────────────────────────────────── */

    function handleTabSwitch(item, panel) {
      const handle = item.dataset.menuHandle;
      if (!handle) return;

      // Active class on level-1 items
      panel.querySelectorAll('.mega-menu__item-level-1').forEach((el) => {
        el.classList.toggle('active', el === item);
      });

      // ── Grandchildren lists ──
      const allLists   = Array.from(panel.querySelectorAll('.grandchildren-list'));
      const targetList = panel.querySelector(`#grandchildren-${handle}`);

      allLists.forEach((list) => {
        if (list === targetList || !list.classList.contains('is-active')) return;
        list.classList.remove('is-active');
        list.classList.add('is-leaving');
        gsap.killTweensOf(list);
        gsap.to(list, {
          opacity: 0,
          duration: 0.15,
          ease: 'power1.in',
          onComplete() {
            list.classList.remove('is-leaving');
            gsap.set(list, { clearProps: 'opacity' });
          },
        });
      });

      if (targetList) {
        clearTimeout(state.tabSwitchTimer);
        // Wait for the leave animation (150ms) to finish before showing new list
        state.tabSwitchTimer = setTimeout(() => {
          targetList.classList.remove('is-leaving');
          targetList.classList.add('is-active');
          gsap.killTweensOf(targetList);
          gsap.fromTo(targetList, { opacity: 0 }, { opacity: 1, duration: 0.20, ease: 'power2.out' });
        }, 160);
      }

      // ── Images ──
      const allImages   = Array.from(panel.querySelectorAll('.mega-menu__image-wrapper'));
      const targetImage = panel.querySelector(`#image-${handle}`);

      allImages.forEach((img) => {
        if (img === targetImage || !img.classList.contains('is-active')) return;
        img.classList.remove('is-active');
        img.classList.add('is-leaving');
        gsap.killTweensOf(img);
        gsap.to(img, {
          opacity: 0,
          duration: 0.22,
          ease: 'power1.in',
          onComplete() {
            img.classList.remove('is-leaving');
            gsap.set(img, { clearProps: 'opacity' });
          },
        });
      });

      if (targetImage) {
        targetImage.classList.remove('is-leaving');
        targetImage.classList.add('is-active');
        gsap.killTweensOf(targetImage);
        gsap.fromTo(targetImage, { opacity: 0 }, { opacity: 1, duration: 0.26, ease: 'power2.out' });
      }
    }

    /* ────────────────────────────────────────────
       EVENTS
    ──────────────────────────────────────────── */

    function onTriggerEnter(trigger) {
      if (!isDesktop()) return;
      clearTimeout(state.closeTimer);
      state.closeTimer = null;
      clearTimeout(state.openTimer);
      // openPanel handles the case where this trigger is already closing
      state.openTimer = setTimeout(
        () => openPanel(trigger),
        state.activeTrigger && state.activeTrigger !== trigger ? 0 : HOVER_OPEN_DELAY
      );
    }

    function onTriggerLeave() {
      if (!isDesktop()) return;
      clearTimeout(state.openTimer);
      state.openTimer = null;
      clearTimeout(state.closeTimer);
      state.closeTimer = setTimeout(closePanel, HOVER_CLOSE_DELAY);
    }

    function onPanelEnter() {
      if (!isDesktop()) return;
      clearTimeout(state.closeTimer);
      state.closeTimer = null;
      // If the current panel is in a close animation, cancel and reverse it
      if (state.activeTrigger) {
        const panel = getPanelOf(state.activeTrigger);
        if (panel && panel.dataset.mmenuClosing) {
          openPanel(state.activeTrigger);
        }
      }
    }

    function onPanelLeave() {
      if (!isDesktop()) return;
      clearTimeout(state.closeTimer);
      state.closeTimer = setTimeout(closePanel, HOVER_CLOSE_DELAY);
    }

    triggers.forEach((trigger) => {
      const panel    = getPanelOf(trigger);
      const tabItems = trigger.querySelectorAll('.mega-menu__item-level-1');

      trigger.addEventListener('mouseenter', () => onTriggerEnter(trigger));
      trigger.addEventListener('mouseleave', onTriggerLeave);

      if (panel) {
        panel.addEventListener('mouseenter', onPanelEnter);
        panel.addEventListener('mouseleave', onPanelLeave);
        tabItems.forEach((item) => {
          item.addEventListener('mouseenter', () => handleTabSwitch(item, panel));
        });
      }
    });

    // Click outside
    document.addEventListener('click', (event) => {
      if (!isDesktop() || !state.activeTrigger) return;
      if (!scope.contains(event.target)) { clearTimers(); closePanel(); }
    });

    // Escape key
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !state.activeTrigger) return;
      const link = getTriggerLink(state.activeTrigger);
      clearTimers();
      closePanel();
      if (link) link.focus();
    });

    // Resize: close on mobile
    let resizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (!isDesktop() && state.activeTrigger) {
          clearTimers();
          closePanelImmediate(state.activeTrigger);
          state.activeTrigger = null;
        }
      }, 100);
    });

    // Initial aria state
    triggers.forEach((trigger) => {
      const link = getTriggerLink(trigger);
      if (link && !link.hasAttribute('aria-expanded')) link.setAttribute('aria-expanded', 'false');
    });
  }

  /* ──────────────────────────────────────────────────
     BOOT
  ────────────────────────────────────────────────── */

  function boot() { waitForGsap(20, init); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', boot);
  }
})();
